import { addDays, format } from 'date-fns';
import { RelevanceDailyStat } from './dashboardApi';
import { createDateBuckets } from './messagesStats';

export interface RelevanceDataset {
  labels: string[];
  values: number[];
}

const DATE_FORMAT = 'yyyy-MM-dd';

export const buildRelevanceDataset = (
  dailyStats: RelevanceDailyStat[],
  startDate: string,
  endDate: string
): RelevanceDataset => {
  const start = parseDate(startDate);
  const end = parseDate(endDate);

  if (!start || !end || start > end) {
    return { labels: [], values: [] };
  }

  const statsMap = normalizeDailyStats(dailyStats);
  const buckets = createDateBuckets(start, end);

  const labels = buckets.map((bucket) => bucket.label);
  const values = buckets.map((bucket) =>
    averageStatsInRange(statsMap, bucket.start, bucket.end)
  );

  return { labels, values };
};

const parseDate = (value: string): Date | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

interface NormalizedRelevanceStat {
  score: number;
  hasData: boolean;
}

const normalizeDailyStats = (dailyStats: RelevanceDailyStat[]) => {
  const statsMap = new Map<string, NormalizedRelevanceStat>();
  dailyStats.forEach((stat) => {
    if (!stat?.date) return;
    const parsed = parseDate(stat.date);
    if (!parsed) return;

    const score = stat.avg_relevance_score ?? 0;
    const hasData =
      typeof stat.question_count === 'number'
        ? stat.question_count > 0
        : (stat.avg_relevance_score ?? null) !== null &&
          stat.avg_relevance_score !== undefined &&
          stat.avg_relevance_score !== 0;

    statsMap.set(format(parsed, DATE_FORMAT), {
      score,
      hasData,
    });
  });
  return statsMap;
};

const averageStatsInRange = (
  statsMap: Map<string, NormalizedRelevanceStat>,
  start: Date,
  end: Date
): number => {
  let current = start;
  let count = 0;
  let total = 0;

  while (current <= end) {
    const key = format(current, DATE_FORMAT);
    const stat = statsMap.get(key);
    if (stat?.hasData) {
      total += stat.score;
      count += 1;
    }
    current = addDays(current, 1);
  }

  if (!count) return 0;
  return Number((total / count).toFixed(1));
};
